import {
  Box,
  Button,
  TextField,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Chip,
} from "@mui/material";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import React, { useEffect, useState } from "react";
import { useLocalStorage } from "../auth/useLocalStorage";
import { CommonService } from "../services/CommonService";
import { IProductionPlanSelection } from "../common/interfaces";
import { InventoryService } from "../services/InventoryService";

interface ProductionsExecuteProps {
  handleCancel: () => void;
  /** Cuando viene, se muestra el botón para regresar al plan del día. */
  onBack?: () => void;
  /** Recetas y cantidades precargadas desde el plan del día. */
  initialSelection?: IProductionPlanSelection[];
}

export const ProductionsExecute = (props: ProductionsExecuteProps) => {
  const { handleCancel, onBack, initialSelection } = props;
  const [user] = useLocalStorage("user", null);

  const handleCloseBox = (event: any) => {
    const formInfo = new FormData(event.currentTarget);
    event.preventDefault();

    if (warehouse === "") {
      CommonService.displayAlert("Debes seleccionar un almacen origen");
      return;
    }
    if (tablajeriaInfo && tablajeriaInfo.length === 0) {
      CommonService.displayAlert("Debe seleccionar un proceso a producir");
      return;
    }
    const data = [];
    if (tablajeriaInfo) {
      for (let index = 0; index < tablajeriaInfo.length; index++) {
        const element = tablajeriaInfo[index];
        data.push({
          name: element.name,
          id: element.id,
          inventory: { cost: element.inventory?.cost },
          // Se llavea por receta, no por producto: hay productos con varias
          // recetas y compartir la llave mandaba la misma cantidad dos veces.
          origin_quantity: productsTablajeo[element.id],
          product_id: element.product.id,
        });
      }
    }
    const completeDate = {
      user_id: user.id,
      is_pos: 1,
      cash_box_id: user.cbox.id,
      warehouse: warehouseList.find(
        (item: any) => item.id.toString() === formInfo.get("warehouse")?.toString()
      ),
      productions: data,
    };

    CommonService.addProductionsProcess(completeDate).then((res) => {
      if (res.success) {
        CommonService.displayAlert(res.message);
        handleCancel();
      } else {
        CommonService.displayAlert(res.message);
      }
    });
  };

  const [process, setProcess] = React.useState<any[]>([]);
  const [warehouse, setWarehouses] = React.useState(`${user?.warehouse_id}` ?? "");
  const [options, setOptions] = React.useState<Record<string, any>>(
    [] as unknown as Record<string, string>
  );
  const [tablajeriaList, setTablajeriaList] = React.useState<any[]>([]);
  const [tablajeriaInfo, setTablajeriaInfo] = React.useState<any[] | undefined>(undefined);
  const [warehouseList, setWarehouseList] = React.useState<any[]>([]);
  const [productsTablajeo, setProductsTablajeo] = useState<Record<string, any>>({});
  const [seeded, setSeeded] = useState(false);

  const handleChange = (event: SelectChangeEvent<any[]>) => {
    setProcess(event.target.value as any[]);
  };

  useEffect(() => {
    CommonService.getProductionProcess().then((res) => {
      if (res.length > 0) {
        setOptions(
          res.map((item: any) => ({
            name: item.name,
            value: item.id,
          }))
        );
        setTablajeriaList(res);
      }
    });
    InventoryService.getWarehouses().then((res) => {
      if (res.length > 0) {
        setWarehouseList(res);
      }
    });
  }, []);

  // Sembrar la selección que viene del plan del día, una sola vez, cuando ya
  // se cargó el catálogo de recetas.
  useEffect(() => {
    if (seeded || tablajeriaList.length === 0) return;
    if (!initialSelection || initialSelection.length === 0) return;

    const preselected: any[] = [];
    const quantities: Record<string, any> = {};

    initialSelection.forEach((sel) => {
      const recipe = tablajeriaList.find((item: any) => item.id === sel.recipeId);
      if (!recipe) return;
      preselected.push({ name: recipe.name, value: recipe.id });
      quantities[recipe.id.toString()] = sel.quantity;
    });

    if (preselected.length > 0) {
      setProcess(preselected);
      setProductsTablajeo(quantities);
    }
    setSeeded(true);
  }, [tablajeriaList, initialSelection, seeded]);

  useEffect(() => {
    if (process.length === 0) {
      setTablajeriaInfo([]);
      return;
    }
    let aux: any[] = [];
    process.forEach((item: any) => {
      const index = tablajeriaList.findIndex((i: any) => i.id === parseInt(item.value));

      if (index > -1) {
        aux.push(tablajeriaList[index]);
      }
    });

    setTablajeriaInfo([...aux]);
  }, [process, tablajeriaList]);

  return (
    <Box component="form" onSubmit={handleCloseBox} noValidate sx={{ mt: 1 }}>
      <Typography variant="h6" component="h2" gutterBottom>
        Proceso de Produccion
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormControl fullWidth style={{ marginTop: 20 }}>
            <InputLabel id="production-process-label">Proceso de Produccion</InputLabel>
            <Select
              labelId="production-process-label"
              id="production-process-select"
              multiple
              value={process}
              name="process"
              input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={"selected_" + value.value} label={value.name} />
                  ))}
                </Box>
              )}
              label="Proceso de Produccion"
              onChange={handleChange}
            >
              {options.map((item: any, index: number) => (
                <MenuItem value={item} key={index}>
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth style={{ marginTop: 20 }}>
            <InputLabel id="production-warehouse-label">Almacen</InputLabel>
            <Select
              labelId="production-warehouse-label"
              id="production-warehouse-select"
              name="warehouse"
              required
              value={warehouse}
              onChange={(e) => setWarehouses(e.target.value)}
              label="Almacen"
            >
              {warehouseList.length > 0 &&
                warehouseList
                  .filter((item) => item.id === user.warehouse_id)
                  .map((item) => (
                    <MenuItem value={item.id} key={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <hr />
      <table className="table table-striped" style={{ marginTop: 20, width: "100%" }}>
        <tbody>
          {tablajeriaInfo &&
            tablajeriaInfo.map((item: any) => (
              <tr key={item.id}>
                <td>
                  <Typography>{item.product.name}</Typography>
                  {/* El nombre de la receta desambigua los productos que tienen más de una */}
                  <Typography variant="caption" color="text.secondary">
                    {item.name}
                  </Typography>
                </td>
                <td>
                  <TextField
                    margin="normal"
                    type="number"
                    required
                    fullWidth
                    value={productsTablajeo[item.id.toString()] ?? ""}
                    onChange={(e) => {
                      setProductsTablajeo({
                        ...productsTablajeo,
                        [item.id.toString()]: e.target.value,
                      });
                    }}
                    label="Cantidad a Producir"
                    name="quantity"
                  />
                </td>
              </tr>
            ))}
        </tbody>
      </table>

      <Button variant="contained" sx={{ mt: 3, mb: 2 }} onClick={() => handleCancel()}>
        Cancelar
      </Button>
      {onBack && (
        <Button variant="outlined" sx={{ mt: 3, mb: 2, ml: 3 }} onClick={onBack}>
          Volver al Plan
        </Button>
      )}
      <Button type="submit" variant="contained" sx={{ mt: 3, mb: 2, ml: 3 }}>
        Guardar y Salir
      </Button>
    </Box>
  );
};
